import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Box,
    Grid,
    Paper,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Tooltip,
    IconButton,
    Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MuiAlert from '@mui/material/Alert';
import { useAuth } from '../context/AuthContext';
import { getCurrentFYear } from '../utils/financialYear';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import Dropdown from '../components/Dropdown';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { FaFileExcel } from 'react-icons/fa';
// import UploadFileIcon from '@mui/icons-material/UploadFile';

export default function PriceListMaster({ connectionString }) {
    const { user, selectedCompany } = useAuth();
    const API_BASE = `${connectionString}/api`;
    const companyId = selectedCompany?.CompId;
    const AppRegID = selectedCompany?.AppRegID;
    const fYear = getCurrentFYear();
    const userId = user?.UserId;

    // State
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
    const [increasedByPct, setIncreasedByPct] = useState('');
    const [decreasedByPct, setDecreasedByPct] = useState('');
    const [selectionModel, setSelectionModel] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteRowId, setDeleteRowId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch product groups
    const fetchGroups = useCallback(async () => {
        if (!companyId) return;
        try {
            const { data } = await axios.get(`${API_BASE}/product-get-data/api/item-group?companyId=${companyId}`);
            console.log(data);
            if (Array.isArray(data)) {
                setGroups(data);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            showSnackbar('Failed to load groups', 'error');
        }
    }, [API_BASE, companyId]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    // Fetch items by group with prices
    const fetchPriceList = useCallback(async () => {
        if (!selectedGroupId) {
            setRows([]);
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_BASE}/priceList/getItemsByGroup/${selectedGroupId}`, {
                params: { companyId, AppRegID },
            });
            if (Array.isArray(data)) {
                const transformedRows = data.map((item, index) => {
                    const fetchedNewPrice = Number(item.NewPrice || 0);
                    return {
                        id: item.ItemCode || index,
                        ItemCode: item.ItemCode || '',
                        description: item.Description || '',
                        unit: item.Unit || 'NOS',
                        lastDate: item.LastDate || newDate,
                        lastPrice: Number(item.LastPrice || item.OldPrice || 0),
                        newPrice: fetchedNewPrice,
                        // Stable base used when Increased/Decreased % is applied,
                        // so repeated % edits recompute from the original value
                        // instead of compounding on top of the last result.
                        baseNewPrice: fetchedNewPrice,
                        remark1: item.Remark1 || '',
                        remark2: item.Remark2 || '',
                        srNo: index + 1,
                    };
                });
                setRows(transformedRows);
                setIsDirty(false);
            }
        } catch (error) {
            console.error('Error fetching price list:', error);
            showSnackbar('Failed to load price list', 'error');
        } finally {
            setLoading(false);
        }
    }, [API_BASE, selectedGroupId, companyId, AppRegID, newDate]);

    useEffect(() => {
        fetchPriceList();
    }, [fetchPriceList]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleEditRow = (row) => {
        setEditingRow({ ...row });
        setOpenEditDialog(true);
    };

    // Popup Save only updates the row locally — it does not hit the database.
    // The actual save happens from the page-level "Save" button (handleSaveAll).
    const handleSaveEdit = () => {
        setRows(prev => prev.map(r => (r.id === editingRow.id ? { ...editingRow } : r)));
        setIsDirty(true);
        setOpenEditDialog(false);
    };

    // Persists every row currently in the grid to the database (insert/update
    // is decided server-side per item, same as before).
    const handleSaveAll = async () => {
        if (rows.length === 0) return;
        try {
            setSaving(true);
            for (const row of rows) {
                await axios.put(`${API_BASE}/priceList/update`, {
                    ItemCode: row.ItemCode,
                    GroupId: selectedGroupId,
                    NewPrice: row.newPrice,
                    LastPrice: row.lastPrice,
                    Remark1: row.remark1,
                    Remark2: row.remark2,
                    LastDate: row.lastDate,
                    companyId,
                    AppRegID,
                    userId,
                    fYear,
                });
            }
            showSnackbar('Price list saved successfully', 'success');
            fetchPriceList();
        } catch (error) {
            console.error('Error saving price list:', error);
            showSnackbar('Failed to save price list', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Recalculates New Price for every row from its stable baseNewPrice using the
    // given Increased/Decreased percentages (each expected to be 1-100).
    const applyPercentageToRows = (increasedPct, decreasedPct) => {
        const incPct = Number(increasedPct) || 0;
        const decPct = Number(decreasedPct) || 0;
        setRows(prev => prev.map(r => ({
            ...r,
            newPrice: Number((r.baseNewPrice * (1 + incPct / 100 - decPct / 100)).toFixed(2)),
        })));
        setIsDirty(true);
    };

    // Increased and Decreased are mutually exclusive — typing in one clears the other.
    const handleIncreasedByPctChange = (value) => {
        setIncreasedByPct(value);
        if (value === '') {
            applyPercentageToRows('', decreasedByPct);
            return;
        }
        setDecreasedByPct('');
        const pct = Math.min(100, Math.max(1, Number(value) || 0));
        applyPercentageToRows(pct, '');
    };

    const handleDecreasedByPctChange = (value) => {
        setDecreasedByPct(value);
        if (value === '') {
            applyPercentageToRows(increasedByPct, '');
            return;
        }
        setIncreasedByPct('');
        const pct = Math.min(100, Math.max(1, Number(value) || 0));
        applyPercentageToRows('', pct);
    };

    const handleDeleteRow = (row) => {
        setDeleteRowId(row.ItemCode);
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API_BASE}/priceList/delete/${deleteRowId}`, {
                params: { companyId, AppRegID, groupId: selectedGroupId },
            });
            showSnackbar('Price deleted successfully', 'success');
            setConfirmOpen(false);
            fetchPriceList();
        } catch (error) {
            console.error('Error deleting price:', error);
            showSnackbar('Failed to delete price', 'error');
        }
    };

    const handleExportExcel = () => {
        if (rows.length === 0) {
            showSnackbar('No data to export', 'warning');
            return;
        }

        try {
            const csvContent = [
                ['Sr.No', 'Item Code', 'Description', 'Unit', 'Last Date', 'Last Price', 'New Price', 'Remark1', 'Remark2'],
                ...rows.map(row => [
                    row.srNo,
                    row.ItemCode,
                    row.description,
                    row.unit,
                    row.lastDate,
                    row.lastPrice,
                    row.newPrice,
                    row.remark1,
                    row.remark2,
                ]),
            ]
                .map(row => row.join(','))
                .join('\n');

            const element = document.createElement('a');
            element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
            element.setAttribute('download', `PriceList_${new Date().toISOString().split('T')[0]}.csv`);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            showSnackbar('Excel exported successfully', 'success');
        } catch (error) {
            console.error('Error exporting:', error);
            showSnackbar('Failed to export', 'error');
        }
    };

    const handleImportExcel = () => {
        fileInputRef.current?.click();
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('groupId', selectedGroupId);
        formData.append('companyId', companyId);
        formData.append('AppRegID', AppRegID);
        formData.append('userId', userId);

        try {
            await axios.post(`${API_BASE}/priceList/import`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            showSnackbar('Price list imported successfully', 'success');
            fetchPriceList();
        } catch (error) {
            console.error('Error importing:', error);
            showSnackbar('Failed to import price list', 'error');
        }
    };

    const columns = [
        { field: 'srNo', headerName: '#', width: 50, align: 'center' },
        { field: 'ItemCode', headerName: 'Item Code', width: 120, flex: 1 },
        { field: 'description', headerName: 'Description', width: 180, flex: 1 },
        { field: 'unit', headerName: 'Unit', width: 80, align: 'center' },
        { field: 'lastDate', headerName: 'Last Date', width: 110, align: 'center' },
        {
            field: 'lastPrice',
            headerName: 'Last Price',
            width: 120,
            align: 'right',
            renderCell: params => `₹${Number(params.row.lastPrice).toFixed(2)}`,
        },
        {
            field: 'newPrice',
            headerName: 'New Price',
            width: 120,
            align: 'right',
            renderCell: params => `₹${Number(params.row.newPrice).toFixed(2)}`,
        },
        { field: 'remark1', headerName: 'Remark1', width: 100, align: 'center' },
        { field: 'remark2', headerName: 'Remark2', width: 100, align: 'center' },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            align: 'center',
            sortable: false,
            renderCell: params => (
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditRow(params.row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteRow(params.row)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 1.5 }}>
            {/* Header Controls */}
            <Paper sx={{ p: 1.5, mb: 2 }}>
                <Grid container spacing={1.5} alignItems="flex-end">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="New Date"
                            type="date"
                            value={newDate}
                            onChange={e => setNewDate(e.target.value)}
                            fullWidth
                            size="small"
                            disabled
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Dropdown
                            id="groupName"
                            label="Group Name"
                            value={selectedGroupId}
                            options={groups}
                            valueKey="ProdGRPID"
                            labelKey="GroupName"
                            onChange={option => setSelectedGroupId(option?.ProdGRPID || '')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            label="Increased (%)"
                            type="number"
                            inputProps={{ min: 1, max: 100 }}
                            value={increasedByPct}
                            onChange={e => handleIncreasedByPctChange(e.target.value)}
                            disabled={decreasedByPct !== ''}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            label="Decreased (%)"
                            type="number"
                            inputProps={{ min: 1, max: 100 }}
                            value={decreasedByPct}
                            onChange={e => handleDecreasedByPctChange(e.target.value)}
                            disabled={increasedByPct !== ''}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Export to Excel">
                                <Button startIcon={<FaFileExcel />} variant="outlined" onClick={handleExportExcel} size="small">
                                    Export
                                </Button>
                            </Tooltip>
                            {/* <Tooltip title="Import">
                                <Button size="small" variant="outlined" onClick={handleImportExcel}>
                                    <UploadFileIcon />
                                </Button>
                            </Tooltip> */}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* DataGrid Table */}
            <Paper sx={{ height: '500px', width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSizeOptions={[25, 50, 100]}
                        checkboxSelection
                        selectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        slots={{ noRowsOverlay: CustomNoDataOverlay }}
                        density="compact"
                        sx={{ border: 'none' }}
                    />
                )}
            </Paper>

            {/* Save Button — appears once any row has changed, saves everything to the database */}
            {isDirty && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveAll}
                        disabled={saving}
                        size="small"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            )}

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs">
                <DialogTitle>Edit Price</DialogTitle>
                <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {editingRow && (
                        <>
                            <TextField label="Item Code" value={editingRow.ItemCode} disabled size="small" />
                            <TextField label="Description" value={editingRow.description} disabled size="small" />
                            <TextField
                                label="Last Price"
                                type="number"
                                value={editingRow.lastPrice}
                                disabled
                                size="small"
                            />
                            <TextField
                                label="New Price"
                                type="number"
                                value={editingRow.newPrice}
                                onChange={e => setEditingRow({ ...editingRow, newPrice: Number(e.target.value) })}
                                size="small"
                            />
                            <TextField
                                label="Remark 1"
                                value={editingRow.remark1}
                                onChange={e => setEditingRow({ ...editingRow, remark1: e.target.value })}
                                disabled
                                size="small"
                            />
                            <TextField
                                label="Remark 2"
                                value={editingRow.remark2}
                                onChange={e => setEditingRow({ ...editingRow, remark2: e.target.value })}
                                disabled
                                size="small"
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveEdit} variant="contained" size="small">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>Delete this price entry?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} variant="contained" color="error" size="small">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Hidden File Input */}
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFileUpload} />

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose}>
                <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
}
